import React from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/atoms'
import { SelectField } from '@/components/molecules/select-field'
import { RepeatableFormSection } from '@/components/molecules/repeatable-form-section'
import { useIsMobile } from '@/shared/hooks/use-is-mobile'
import {
  PET_AGE_OPTIONS,
  PET_TYPE_OPTIONS,
} from '@/shared/constants/onboarding'

export interface PetDetails {
  id?: string
  name: string
  petType: string
  age: string
  breed: string
}

interface PetFormSectionProps {
  entries: PetDetails[]
  title: (index: number) => string
  onAdd: () => void
  onRemove: (index: number) => void
  onUpdate: (index: number, field: keyof PetDetails, value: string) => void
  getBreedOptions: (petType: string) => { value: string; label: string }[]
  addLabel: string
  showAdd?: boolean
  showErrors?: boolean
  icon?: React.ReactNode
}

const FIELD_COL = 'flex-1 md:max-w-66.5 md:min-w-50'

export function PetFormSection({
  entries,
  title,
  onAdd,
  onRemove,
  onUpdate,
  getBreedOptions,
  addLabel,
  showAdd,
  showErrors,
  icon,
}: PetFormSectionProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  return (
    <RepeatableFormSection
      entries={entries}
      title={title}
      onAdd={onAdd}
      onRemove={onRemove}
      addLabel={addLabel}
      addMoreOutWrapper={true}
      showAdd={showAdd}
      headerIcon={icon}
      renderFields={(entry, i) => {
        const hasAnyField = !!(
          entry.name ||
          entry.petType ||
          entry.age ||
          entry.breed
        )
        const fieldError = (value: string) =>
          showErrors && hasAnyField && !value ? t('common.required') : undefined
        return (
          <>
            <div className={FIELD_COL}>
              <Input
                label={t('onboarding.renters.form.whoIsCovered.name')}
                placeholder={t(
                  'onboarding.renters.form.whoIsCovered.namePlaceholderPet',
                )}
                value={entry.name}
                onChange={(e) => onUpdate(i, 'name', e.target.value)}
                error={fieldError(entry.name)}
              />
            </div>
            <div className={FIELD_COL}>
              <SelectField
                label={t('onboarding.renters.form.whoIsCovered.petType')}
                value={entry.petType}
                placeholder={t(
                  'onboarding.renters.form.whoIsCovered.petTypePlaceholder',
                )}
                options={PET_TYPE_OPTIONS.map((o) => ({
                  ...o,
                  label: t(o.label),
                }))}
                onSelect={(v) => onUpdate(i, 'petType', v)}
                error={fieldError(entry.petType)}
              />
            </div>
            <div className={FIELD_COL}>
              <SelectField
                label={t('onboarding.renters.form.whoIsCovered.petAge')}
                value={entry.age}
                placeholder={t(
                  'onboarding.renters.form.whoIsCovered.petAgePlaceholder',
                )}
                options={PET_AGE_OPTIONS.map((o) => ({
                  ...o,
                  label: t(o.label),
                }))}
                onSelect={(v) => onUpdate(i, 'age', v)}
                error={fieldError(entry.age)}
              />
            </div>
            <div className={FIELD_COL}>
              <SelectField
                label={t('onboarding.renters.form.whoIsCovered.petBreed')}
                value={entry.breed}
                placeholder={t(
                  'onboarding.renters.form.whoIsCovered.petBreedPlaceholder',
                )}
                options={getBreedOptions(entry.petType)}
                searchable
                disabled={!entry.petType}
                dropdownPosition={
                  isMobile && i === entries.length - 1 ? 'relative' : 'absolute'
                }
                onSelect={(v) => onUpdate(i, 'breed', v)}
                error={fieldError(entry.breed)}
              />
            </div>
          </>
        )
      }}
    />
  )
}
